import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCurrency } from "../App";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, ArrowRight, Check, Loader2, Smartphone, Sparkles,
  UtensilsCrossed, Dumbbell, Leaf, MountainSnow, Building2,
  MapPin, Globe2, Calendar, Compass, Heart,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (url) => (url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "/logo-black.png");

const COUNTRY_CODES = [
  { code: "+91", label: "IN +91" }, { code: "+1", label: "US +1" }, { code: "+44", label: "UK +44" },
  { code: "+971", label: "AE +971" }, { code: "+61", label: "AU +61" }, { code: "+65", label: "SG +65" },
  { code: "+81", label: "JP +81" }, { code: "+49", label: "DE +49" }, { code: "+33", label: "FR +33" },
  { code: "+86", label: "CN +86" },
];

const PILLARS = [
  {
    icon: UtensilsCrossed, color: "from-green-500 to-emerald-600",
    title: "QSRs",
    body: "Everyday access to clean, balanced meals — superfoods built into the dishes you already crave.",
  },
  {
    icon: Dumbbell, color: "from-emerald-500 to-teal-600",
    title: "Specialized Meal Plans",
    body: "Structured nutrition for fitness, performance, and recovery — delivered with the precision your body deserves.",
  },
  {
    icon: Leaf, color: "from-lime-500 to-green-600",
    title: "Wellness Brands",
    body: "Kombucha. Green Tea. Kimchi. Functional, gut-friendly daily consumables that quietly upgrade your routine.",
  },
  {
    icon: MountainSnow, color: "from-teal-500 to-cyan-600",
    title: "Wellness Retreats & Getaways",
    body: "Deep reset experiences for mind and body — where the lifestyle becomes immersive, not optional.",
  },
  {
    icon: Building2, color: "from-green-600 to-emerald-700",
    title: "Institutional Dining",
    body: "Bringing the Superfood Lifestyle to workplaces, campuses, and communities — at scale.",
  },
];

const MARKETS = ["Karnataka", "Maharashtra", "Goa", "Uttar Pradesh"];

const APP_FEATURES = [
  { icon: UtensilsCrossed, title: "Daily Ordering", body: "Reliable, fast, designed around your routine." },
  { icon: Dumbbell, title: "Meal Plan Access", body: "Track, adjust and stick to plans built for results." },
  { icon: Leaf, title: "Product Discovery", body: "Curated wellness brands at your fingertips." },
  { icon: Heart, title: "Habit Building", body: "Small daily nudges that compound into transformation." },
];

const INTERESTS = ["Franchise", "Institutional Dining", "Partnerships", "General"];

// Lightweight intersection-observer hook for scroll reveals
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.unobserve(node); } },
      { rootMargin: "-10% 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export default function DemoEnquiry() {
  const navigate = useNavigate();
  const { logoUrl } = useCurrency();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country_code: "+91",
    area_of_interest: "Franchise", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const ecosystemRef = useRef(null);
  const enquiryRef = useRef(null);
  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) setSubmitted(true);
      else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || "Something went wrong");
      }
    } catch {
      alert("Network error. Please try again.");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-green-900 to-emerald-950 flex items-center justify-center px-6" data-testid="enquiry-success">
        <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" strokeWidth={3} />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Welcome to the Movement</h1>
          <p className="text-gray-500 mb-6">We have received your enquiry and our team will reach out shortly to take this conversation forward.</p>
          <Button onClick={() => navigate("/")} className="bg-green-600 hover:bg-green-700 w-full" data-testid="back-home-btn">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf6] text-gray-900" data-testid="lifestyle-landing">
      {/* ===== Sticky Top Nav ===== */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm" data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <img src={logoSrc(logoUrl)} alt="Super Morsel" className="h-24 sm:h-28 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          <div className="hidden sm:flex items-center gap-5 text-xs font-semibold text-gray-600">
            <button onClick={() => scrollTo(ecosystemRef)} className="hover:text-green-700">Ecosystem</button>
            <button onClick={() => scrollTo(enquiryRef)} className="hover:text-green-700">Partner</button>
          </div>
        </div>
      </div>

      {/* ===== 1. HERO ===== */}
      <section className="relative overflow-hidden bg-emerald-900" data-testid="hero-section">
        <div className="absolute inset-0 bg-emerald-900" />
        {/* Decorative blobs */}
        <div className="absolute -top-32 -right-24 w-96 h-96 bg-green-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-16 w-[28rem] h-[28rem] bg-emerald-400/15 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 py-24 sm:py-32 text-center text-white">
          <Reveal>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur-md text-[11px] tracking-[0.2em] font-semibold uppercase px-4 py-1.5 rounded-full">
              <Sparkles size={12} /> A Movement, Not a Menu
            </span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-6 text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight">
              Live the
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-green-300 via-emerald-300 to-lime-200">Superfood Lifestyle</span>
            </h1>
          </Reveal>
          <Reveal delay={250}>
            <p className="mt-6 text-base sm:text-lg lg:text-xl text-green-50/90 max-w-2xl mx-auto leading-relaxed">
              Everyday nutrition made effortless. A wellness ecosystem designed for modern, busy, ambitious lives.
            </p>
          </Reveal>
          <Reveal delay={400}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
              <button
                onClick={() => navigate("/")}
                className="group inline-flex items-center justify-center gap-2 bg-white text-emerald-900 font-bold px-7 py-4 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.99] transition"
                data-testid="hero-download-cta"
              >
                <Smartphone size={18} /> Download App
                <ArrowRight size={16} className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-1 transition-all" />
              </button>
              <button
                onClick={() => scrollTo(ecosystemRef)}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold px-7 py-4 rounded-full backdrop-blur-md transition"
                data-testid="hero-explore-cta"
              >
                <Compass size={18} /> Explore Ecosystem
              </button>
            </div>
          </Reveal>
        </div>
        {/* Soft fade into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#fafaf6] pointer-events-none" />
      </section>

      {/* ===== 2. IDEOLOGY ===== */}
      <section className="relative py-20 sm:py-28" data-testid="ideology-section">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <p className="text-center text-[11px] tracking-[0.3em] font-semibold uppercase text-green-700">The Ideology</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight text-center leading-[1.1]">
              Superfood isn't a trend.
              <span className="block text-green-700">It's a way of life.</span>
            </h2>
          </Reveal>
          <Reveal delay={250}>
            <p className="mt-6 text-base sm:text-lg text-gray-600 text-center max-w-2xl mx-auto leading-relaxed">
              Wellness isn't built in a sprint. It's built in the small, repeatable choices you make every single day. We make those choices effortless.
            </p>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-3 gap-5">
            {[
              { title: "Convenience meets nutrition", body: "Real meals for real lives — nothing extreme, nothing performative." },
              { title: "Designed for busy living", body: "Order, plan, and consume on your schedule, not against it." },
              { title: "Habits that compound", body: "Small daily inputs. Long-term transformation that actually shows up." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={150 + i * 120}>
                <div className="h-full bg-white border border-gray-100 rounded-2xl p-6 hover:border-green-200 hover:shadow-md transition-all">
                  <div className="w-10 h-10 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center mb-4">
                    <Check className="text-green-600" size={20} strokeWidth={3} />
                  </div>
                  <h3 className="font-extrabold text-gray-900 mb-1.5">{c.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 3. ECOSYSTEM / VERTICALS ===== */}
      <section ref={ecosystemRef} className="relative py-20 sm:py-28 bg-gradient-to-b from-white to-green-50/40" data-testid="ecosystem-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <p className="text-center text-[11px] tracking-[0.3em] font-semibold uppercase text-green-700">The Ecosystem</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight text-center leading-[1.1]">
              Five pillars. <span className="text-green-700">One lifestyle.</span>
            </h2>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-5 text-base sm:text-lg text-gray-600 text-center max-w-2xl mx-auto leading-relaxed">
              Each vertical is a different way to live the same ideology — wherever you are, whatever your day demands.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 100}>
                  <div className="group h-full relative bg-white border border-gray-100 rounded-2xl p-6 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-5 shadow-md group-hover:scale-110 transition-transform`}>
                      <Icon className="text-white" size={22} />
                    </div>
                    <h3 className="font-extrabold text-lg text-gray-900 mb-2">{p.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{p.body}</p>
                    <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Reveal>
              );
            })}
            {/* Filler: highlight panel */}
            <Reveal delay={500}>
              <div className="h-full bg-gradient-to-br from-emerald-700 to-green-900 rounded-2xl p-6 text-white relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                <Sparkles size={22} className="opacity-80 mb-5" />
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-green-200 mb-2">Live the Lifestyle</p>
                <p className="text-lg font-extrabold leading-snug">
                  Every pillar feeds the same outcome — eat better, live better, feel sharper.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== 4. EXPANSION ===== */}
      <section className="relative py-20 sm:py-28 bg-gradient-to-br from-emerald-950 via-green-900 to-teal-950 text-white overflow-hidden" data-testid="expansion-section">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(74,222,128,0.4), transparent 40%), radial-gradient(circle at 80% 70%, rgba(20,184,166,0.4), transparent 40%)" }} />
        <div className="relative max-w-5xl mx-auto px-6">
          <Reveal>
            <p className="text-center text-[11px] tracking-[0.3em] font-semibold uppercase text-green-300">Expansion</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight text-center leading-[1.1]">
              Taking the lifestyle <span className="text-green-300">to more people, everywhere.</span>
            </h2>
          </Reveal>

          <Reveal delay={250}>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto">
              {MARKETS.map((m) => (
                <div key={m} className="bg-white/5 border border-white/10 rounded-2xl px-4 py-5 text-center backdrop-blur-md hover:bg-white/10 transition" data-testid={`market-${m.toLowerCase().replace(/\s+/g, "-")}`}>
                  <MapPin size={16} className="mx-auto mb-2 text-green-300" />
                  <span className="text-sm font-bold">{m}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={400}>
            <div className="mt-10 flex items-center justify-center gap-3 text-green-100/90">
              <Globe2 size={18} className="text-green-300" />
              <span className="text-sm sm:text-base">Expanding rapidly across India and globally.</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 5. APP ECOSYSTEM ===== */}
      <section className="relative py-20 sm:py-28" data-testid="app-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal>
                <p className="text-[11px] tracking-[0.3em] font-semibold uppercase text-green-700">The App</p>
              </Reveal>
              <Reveal delay={120}>
                <h2 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]">
                  Your control center for <span className="text-green-700">the lifestyle.</span>
                </h2>
              </Reveal>
              <Reveal delay={220}>
                <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed">
                  Built around how real people actually eat, train, and live. One place to make consistency feel inevitable.
                </p>
              </Reveal>
              <Reveal delay={350}>
                <button
                  onClick={() => navigate("/")}
                  className="mt-8 group inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold text-xs sm:text-sm px-5 sm:px-7 py-3.5 sm:py-4 rounded-full shadow-lg hover:shadow-xl transition whitespace-nowrap"
                  data-testid="app-download-cta"
                >
                  <Smartphone size={16} className="shrink-0" /> Start Living the Superfood Lifestyle
                  <ArrowRight size={14} className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-1 transition-all shrink-0" />
                </button>
              </Reveal>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {APP_FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal key={f.title} delay={i * 90}>
                    <div className="h-full bg-white border border-gray-100 rounded-2xl p-5 hover:border-green-200 hover:shadow-md transition-all">
                      <div className="w-10 h-10 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center mb-3">
                        <Icon className="text-green-700" size={20} />
                      </div>
                      <h3 className="font-extrabold text-gray-900 mb-1 text-sm">{f.title}</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">{f.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== 6. ENQUIRY ===== */}
      <section ref={enquiryRef} className="relative py-20 sm:py-28 bg-gradient-to-b from-green-50/40 to-white" data-testid="enquiry-section">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <p className="text-center text-[11px] tracking-[0.3em] font-semibold uppercase text-green-700">Partner With Us</p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight text-center leading-[1.1]">
              Build the Superfood Lifestyle <span className="text-green-700">with us.</span>
            </h2>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-5 text-base text-gray-600 text-center max-w-xl mx-auto leading-relaxed">
              This isn't just a business enquiry — it's an invitation to join a movement reshaping how people eat, every single day.
            </p>
          </Reveal>

          <Reveal delay={350}>
            <form
              onSubmit={handleSubmit}
              className="mt-10 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8 space-y-5"
              data-testid="enquiry-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] text-gray-500 mb-1.5 block">Full Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Your full name"
                    required
                    data-testid="enquiry-name"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500 mb-1.5 block">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="you@example.com"
                    required
                    data-testid="enquiry-email"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[10px] text-gray-500 mb-1.5 block">Phone *</Label>
                <div className="flex gap-2">
                  <select
                    value={form.country_code}
                    onChange={(e) => setForm((p) => ({ ...p, country_code: e.target.value }))}
                    className="h-10 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="enquiry-country-code"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value.replace(/[^0-9]/g, "") }))}
                    placeholder="9876543210"
                    required
                    className="flex-1"
                    data-testid="enquiry-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[10px] text-gray-500 mb-1.5 block">Area of Interest *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  {INTERESTS.map((it) => (
                    <button
                      type="button"
                      key={it}
                      onClick={() => setForm((p) => ({ ...p, area_of_interest: it }))}
                      className={`w-full px-3 py-2 rounded-xl text-xs font-semibold border transition ${
                        form.area_of_interest === it
                          ? "bg-green-600 text-white border-green-600 shadow-sm"
                          : "bg-white text-gray-600 border-gray-200 hover:border-green-300"
                      }`}
                      data-testid={`interest-${it.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {it}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[10px] text-gray-500 mb-1.5 block">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  placeholder="Tell us how you'd like to be part of the movement…"
                  rows={4}
                  data-testid="enquiry-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting || !form.name.trim() || !form.email.trim() || !form.phone.trim()}
                className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full"
                data-testid="enquiry-submit-btn"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Join the Movement"}
              </Button>

              <p className="text-[11px] text-gray-400 text-center flex items-center justify-center gap-1">
                <Calendar size={11} /> We respond within 24 business hours.
              </p>
            </form>
          </Reveal>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col items-center gap-5 text-center">
          <img src={logoSrc(logoUrl)} alt="Super Morsel" className="h-28 sm:h-32 object-contain opacity-90" onError={(e) => { e.target.style.display = "none"; }} />
          <p className="text-[11px] text-gray-400 tracking-wide">Live the Superfood Lifestyle.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-green-700 transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <span className="text-gray-300">·</span>
            <Link to="/refund-policy" className="text-gray-500 hover:text-green-700 transition-colors" data-testid="footer-refund-link">Refund Policy</Link>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">© {new Date().getFullYear()} Super Morsel. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
